import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Icon,
  Metric,
  SectionTabs,
  viLabel,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { BarChart, type BarData } from '@/components/charts/BarChart';
import { HorizontalBar } from '@/components/charts/HorizontalBar';
import { ExportButton } from '@/components/common/ExportButton';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import { useMockTable } from '@/hooks/useMockTable';
import { useForecastResults } from '@/hooks/useForecast';
import { reportService } from '@/services/reportService';
import { formatNumber, toISODate } from '@/lib/utils';
import { useState } from 'react';

type Section = 'customer' | 'product' | 'tier' | 'event';

const HORIZON_DAYS = 14;

export function ForecastWarehousePage() {
  const { isMobile, vw } = useViewport();
  const { currentWarehouse } = useScope();
  const navigate = useNavigate();
  const customers = useMockTable('customers');
  const results = useForecastResults({ warehouseId: currentWarehouse?.id });
  const [section, setSection] = useState<Section>('customer');

  const today = toISODate(new Date());
  const horizonEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + HORIZON_DAYS - 1);
    return toISODate(d);
  }, [today]);

  const horizonResults = useMemo(
    () => results.filter((r) => r.date >= today && r.date <= horizonEnd),
    [results, today, horizonEnd],
  );

  const totalQty = horizonResults.reduce((s, r) => s + r.forecast_qty, 0);
  const totalHours = horizonResults.reduce((s, r) => s + r.total_workload_hours, 0);
  const totalRows = horizonResults.length;
  const dailyAvg = totalQty / Math.max(1, HORIZON_DAYS);

  const byCustomer = useMemo(
    () => reportService.byCustomer({ results: horizonResults, customers }),
    [horizonResults, customers],
  );
  const byProduct = useMemo(
    () => reportService.byProductGroup({ results: horizonResults, customers }),
    [horizonResults, customers],
  );
  const byTier = useMemo(
    () => reportService.byTier({ results: horizonResults, customers }),
    [horizonResults, customers],
  );
  const byEvent = useMemo(() => reportService.byEventType(horizonResults), [horizonResults]);
  const byDate = useMemo(() => reportService.byDate(horizonResults), [horizonResults]);

  const exportRows = byCustomer.map((b) => ({
    customer: b.label,
    forecast_qty: b.qty,
    workload_hours: b.workload_hours.toFixed(1),
    share_pct: b.share_pct.toFixed(1),
    rows: b.count,
  }));

  const dateChartData: BarData[] = byDate.map((d) => {
    const isPeak = horizonResults.some(
      (r) =>
        r.date === d.key &&
        (r.event_type === 'MEGA_SALE' || r.event_type === 'DOUBLE_DAY'),
    );
    const isLift = horizonResults.some(
      (r) =>
        r.date === d.key &&
        ['PAYDAY', 'MID_MONTH', 'MINI_SPIKE'].includes(r.event_type),
    );
    return {
      label: d.key.slice(8),
      value: d.qty,
      tone: isPeak ? 'critical' : isLift ? 'warning' : 'default',
      highlight: d.key === today,
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Lập kế hoạch', 'Dự báo', 'Kho']}
        title={`Báo cáo kho · ${currentWarehouse?.code ?? '—'}`}
        subtitle={
          currentWarehouse
            ? `${currentWarehouse.name} · ${HORIZON_DAYS} ngày tới · capacity ${formatNumber(currentWarehouse.capacity_orders_per_day)} đơn/ngày`
            : 'Chọn kho ở thanh trên'
        }
        actions={
          <>
            <Button
              variant="outline"
              icon="forecast"
              onClick={() => navigate('/planner/forecast')}
            >
              Tổng quan
            </Button>
            <ExportButton
              rows={exportRows}
              columns={[
                { header: 'Khách hàng', key: 'customer' },
                { header: 'Đơn dự báo', key: 'forecast_qty' },
                { header: 'Workload (giờ)', key: 'workload_hours' },
                { header: '% chia sẻ', key: 'share_pct' },
                { header: 'Rows', key: 'rows' },
              ]}
              filename={`forecast-${currentWarehouse?.code ?? 'wh'}-${today}`}
              label="Xuất báo cáo"
            />
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        {totalRows === 0 ? (
          <Card>
            <div
              style={{
                padding: 32,
                textAlign: 'center',
                color: 'var(--bx-text-muted)',
                fontSize: 13,
              }}
            >
              <Icon
                name="forecast"
                size={32}
                style={{ color: 'var(--bx-text-muted)', marginBottom: 12, opacity: 0.5 }}
              />
              <div>Chưa có dự báo cho 14 ngày tới.</div>
              <div style={{ marginTop: 6 }}>
                <Button
                  variant="primary"
                  icon="rocket"
                  onClick={() => navigate('/planner/forecast')}
                >
                  Chạy dự báo ngay
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gap: 12,
                marginBottom: 16,
                gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              }}
            >
              <Metric
                label="Tổng đơn"
                value={formatNumber(totalQty)}
                sublabel={`${HORIZON_DAYS} ngày tới`}
              />
              <Metric
                label="Trung bình ngày"
                value={formatNumber(Math.round(dailyAvg))}
                sublabel={
                  currentWarehouse
                    ? `${((dailyAvg / currentWarehouse.capacity_orders_per_day) * 100).toFixed(1)}% capacity`
                    : '—'
                }
                deltaTone={
                  currentWarehouse && dailyAvg > currentWarehouse.capacity_orders_per_day * 0.85
                    ? 'down'
                    : 'flat'
                }
              />
              <Metric
                label="Workload tổng"
                value={`${formatNumber(Math.round(totalHours))}h`}
                sublabel={`≈ ${(totalHours / 8).toFixed(1)} ca`}
              />
              <Metric
                label="Số khách hàng"
                value={byCustomer.length}
                sublabel="có forecast"
              />
            </div>

            <Card title="Xu hướng đơn 14 ngày" style={{ marginBottom: 16 }}>
              <BarChart data={dateChartData} height={180} />
            </Card>

            <Card padding={0} style={{ marginBottom: 16 }}>
              <div
                style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--bx-border-muted)',
                }}
              >
                <SectionTabs
                  value={section}
                  onChange={(s) => setSection(s as Section)}
                  tabs={[
                    { id: 'customer', label: 'Khách hàng', idx: 1, count: byCustomer.length },
                    { id: 'product', label: 'Nhóm sản phẩm', idx: 2, count: byProduct.length },
                    { id: 'tier', label: 'Tier', idx: 3, count: byTier.length },
                    { id: 'event', label: 'Sự kiện', idx: 4, count: byEvent.length },
                  ]}
                />
              </div>
              <div style={{ padding: 20 }}>
                {section === 'customer' && (
                  <HorizontalBar
                    data={byCustomer.map((c) => ({
                      label: c.label,
                      value: c.qty,
                      hint: `${c.workload_hours.toFixed(1)}h workload · ${c.count} ngày`,
                    }))}
                  />
                )}
                {section === 'product' && (
                  <HorizontalBar
                    data={byProduct.map((p) => ({
                      label: viLabel(p.label),
                      value: p.qty,
                      hint: `${p.workload_hours.toFixed(1)}h workload`,
                    }))}
                  />
                )}
                {section === 'tier' && (
                  <HorizontalBar
                    data={byTier.map((t) => ({
                      label: viLabel(t.label),
                      value: t.qty,
                      hint: `${t.count} rows`,
                    }))}
                  />
                )}
                {section === 'event' && (
                  <HorizontalBar
                    data={byEvent.map((e) => ({
                      label: viLabel(e.label),
                      value: e.qty,
                      hint: `${e.count} cell · ${e.workload_hours.toFixed(1)}h`,
                    }))}
                  />
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
